import React from 'react'
import type { RangePickerProps } from 'antd/es/date-picker'
import DatePicker from 'antd/es/date-picker'
import { getRangePresets, LTD_START_DATE } from '../../utils/time.ts'
import dayjs, { Dayjs } from 'dayjs'
import { useFixedBreakpoint } from '../hooks/use-is-mobile.ts'
import type {
  DateRange,
  MobileRangePreset,
} from './ideya-mobile-range-calendar.tsx'

const { RangePicker } = DatePicker

// Lazy so desktop never downloads antd-mobile JS/CSS.
const IdeyaMobileRangeCalendar = React.lazy(
  () => import('./ideya-mobile-range-calendar.tsx')
)

interface IdeyaRangePickerProps extends React.ComponentProps<
  typeof RangePicker
> {
  disableHistoricalDates?: boolean
  /** Latest selectable date on mobile. Defaults to the end of the current year so preset ranges fit. */
  maxDate?: Dayjs
}

type PickerDisabledDate = NonNullable<RangePickerProps['disabledDate']>

// Measured slide-out is ~550ms; the fallback only matters if afterClose never fires.
const CLOSE_FLUSH_FALLBACK_MS = 1000

function toDateRange(value: RangePickerProps['value']): DateRange | null {
  return value?.[0] && value?.[1] ? [value[0], value[1]] : null
}

const IdeyaRangePicker: React.FC<IdeyaRangePickerProps> = ({
  disableHistoricalDates,
  maxDate,
  ...props
}) => {
  const rangePresets: RangePickerProps['presets'] = getRangePresets()
  const screens = useFixedBreakpoint()
  const isMobile = !screens.md
  const [mobileOpen, setMobileOpen] = React.useState(false)

  const disabledDate: PickerDisabledDate | undefined = disableHistoricalDates
    ? (current: Dayjs) => current && current < dayjs().startOf('day')
    : props.disabledDate

  const presets = (props.presets ?? rangePresets) as MobileRangePreset[]

  // Callers re-render whole pages on change, so apply it after the close animation instead of during it.
  const pendingRangeRef = React.useRef<DateRange | null>(null)
  const onChangeRef = React.useRef(props.onChange)
  onChangeRef.current = props.onChange
  const formatRef = React.useRef(props.format)
  formatRef.current = props.format

  // Shown in the input while the caller's (slow) onChange is still pending.
  const [pendingDisplay, setPendingDisplay] = React.useState<DateRange | null>(
    null
  )

  const flushPendingRange = React.useCallback(() => {
    const range = pendingRangeRef.current
    if (!range) return
    pendingRangeRef.current = null
    const format =
      typeof formatRef.current === 'string' ? formatRef.current : 'YYYY-MM-DD'
    onChangeRef.current?.(range, [
      range[0].format(format),
      range[1].format(format),
    ])
    setPendingDisplay(null)
  }, [])

  // afterClose depends on the spring settling; fall back after the animation should have ended.
  const flushTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined)
  const flushNow = React.useCallback(() => {
    clearTimeout(flushTimerRef.current)
    flushPendingRange()
  }, [flushPendingRange])

  React.useEffect(() => flushNow, [])

  const handleMobileSelect = (range: DateRange) => {
    pendingRangeRef.current = range
    setPendingDisplay(range)
    setMobileOpen(false)
    clearTimeout(flushTimerRef.current)
    flushTimerRef.current = setTimeout(
      flushPendingRange,
      CLOSE_FLUSH_FALLBACK_MS
    )
  }

  // disableHistoricalDates is already covered by `min`; only forward caller rules.
  const isMobileDateDisabled = props.disabledDate
    ? (day: Dayjs) => props.disabledDate!(day, { type: 'date' })
    : undefined

  if (isMobile) {
    const today = dayjs()
    return (
      <div style={{ width: '100%' }}>
        <RangePicker
          allowClear={false}
          disabledDate={disabledDate}
          {...props}
          value={pendingDisplay ?? props.value}
          presets={undefined}
          inputReadOnly
          open={false}
          onOpenChange={(nextOpen) => nextOpen && setMobileOpen(true)}
        />
        <React.Suspense fallback={null}>
          <IdeyaMobileRangeCalendar
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            afterClose={flushNow}
            value={toDateRange(props.value)}
            presets={presets}
            min={disableHistoricalDates ? today : dayjs(LTD_START_DATE)}
            max={maxDate ?? today.endOf('year')}
            isDateDisabled={isMobileDateDisabled}
            onSelect={handleMobileSelect}
          />
        </React.Suspense>
      </div>
    )
  }

  return (
    <div style={{ width: '100%' }}>
      <RangePicker
        allowClear={false}
        disabledDate={disabledDate}
        presets={rangePresets}
        {...props}
      />
    </div>
  )
}

export default IdeyaRangePicker
